export const TERMINAL_STATUSES = new Set([
  "cerrado",
  "archivado",
  "cancelado",
  "desestimado",
]);
export const CIVIL_PROCESS_STATUS_LABELS = {
  evaluacion: "Evaluación",
  preparacion: "Preparación",
  presentado: "Presentado",
  en_litigio: "En litigio",
  resuelto: "Resuelto",
  cerrado: "Cerrado",
  archivado: "Archivado",
  cancelado: "Cancelado",
};
// Backward-compatible export for components that still consume the legacy name.
export const CIVIL_MATTER_STATUS_LABELS = CIVIL_PROCESS_STATUS_LABELS;
const aliases = {
  closed: "cerrado",
  archived: "archivado",
  cancelled: "cancelado",
  dismissed: "desestimado",
  activo: "en_litigio",
  en_proceso: "en_litigio",
  "en proceso": "en_litigio",
  resolved: "resuelto",
};
export const normalizeStatus = (value = "") => {
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return aliases[normalized] || normalized;
};
export const isTerminalMatter = (matter) =>
  TERMINAL_STATUSES.has(
    normalizeStatus(matter?.estado_proceso ?? matter?.estado ?? matter?.status),
  );
const dueDate = (value) =>
  value ? new Date(`${String(value).slice(0, 10)}T12:00:00`) : null;

/** Single client-side source for the operational health of a Civil matter. */
export const calculateCivilMatterOperationalStatus = (
  matter,
  now = new Date(),
) => {
  if (!matter || isTerminalMatter(matter))
    return {
      key: "cerrado",
      label: "Cerrado",
      tone: "closed",
      priority: 99,
      reasons: [],
      missingFields: [],
      requiresAttention: false,
    };
  const reasons = [];
  const missingFields = [];
  const due = dueDate(matter.proxima_fecha);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  if (due && due < today)
    reasons.push({ key: "overdue", label: "Plazo vencido", priority: 0 });
  else if (due && due <= new Date(today.getTime() + 7 * 86400000))
    reasons.push({
      key: "deadline",
      label: "Próximo vencimiento",
      priority: 1,
    });
  if (matter.has_overdue_high_priority_task)
    reasons.push({
      key: "high_priority_task",
      label: "Tarea alta vencida",
      priority: 0,
    });
  if (matter.audience_preparation_pending)
    reasons.push({
      key: "audience_preparation",
      label: "Audiencia próxima sin preparación",
      priority: 1,
    });
  if (
    !matter.abogado_responsable &&
    ["presentado", "en_litigio"].includes(
      normalizeStatus(matter.estado_proceso),
    )
  )
    reasons.push({
      key: "responsible",
      label: "Expediente activo sin responsable",
      priority: 1,
    });
  if (!matter.numero_caso) missingFields.push("Número de caso");
  if (!matter.tribunal) missingFields.push("Tribunal");
  if (matter.requires_judge_assignment && !(matter.judge_id || matter.juez || matter.magistrado))
    missingFields.push("Juez");
  if (!matter.distrito || !matter.division)
    missingFields.push("Distrito/división");
  if (
    !matter.abogado_responsable &&
    !["presentado", "en_litigio"].includes(
      normalizeStatus(matter.estado_proceso),
    )
  )
    missingFields.push("Abogado responsable");
  reasons.sort((a, b) => a.priority - b.priority);
  if (!reasons.length && !missingFields.length)
    return {
      key: "sin_alertas",
      label: "Sin alertas",
      tone: "ok",
      priority: 99,
      reasons,
      missingFields,
      requiresAttention: false,
    };
  if (reasons[0]?.key === "overdue" || reasons[0]?.key === "high_priority_task")
    return {
      key: "vencido",
      label: "Vencido",
      tone: "urgent",
      priority: 0,
      reasons,
      missingFields,
      requiresAttention: true,
    };
  if (reasons.length)
    return {
      key: "proximo_vencimiento",
      label: "Próximo vencimiento",
      tone: "deadline",
      priority: reasons[0].priority,
      reasons,
      missingFields,
      requiresAttention: true,
    };
  return {
    key: "informacion_incompleta",
    label: "Información incompleta",
    tone: "info",
    priority: 99,
    reasons,
    missingFields,
    requiresAttention: false,
  };
};
export const hasUpcomingCivilDeadline = (matter, now = Date.now()) =>
  calculateCivilMatterOperationalStatus(matter, new Date(now)).key ===
  "proximo_vencimiento";
export const requiresCivilMatterAttention = (matter, now = Date.now()) =>
  !isTerminalMatter(matter) &&
  calculateCivilMatterOperationalStatus(matter, new Date(now))
    .requiresAttention;
